import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import PageTitle from "../../layouts/PageTitle";
import { createFaq, updateFaq, getFaqById } from "../../../services/FaqService";
import { getCountries } from "../../../services/CountryService"; // ✅ new
import { Button } from "react-bootstrap";
import { useLocation } from 'react-router-dom';

const multiSelectComponents = {
  MultiValue: ({ data, removeProps }) => (
    <div
      className="d-flex align-items-center"
      style={{
        background: "#EEF2FF",
        borderRadius: "16px",
        padding: "2px 8px",
        margin: "2px",
        maxWidth: "180px",
      }}
    >
      <span
        style={{
          fontSize: "12px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {data.label}
      </span>

      <span
        {...removeProps}
        style={{
          marginLeft: "6px",
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        ×
      </span>
    </div>
  ),
};

const AddFaq = () => {
  const [formData, setFormData] = useState({
    title: "",
    desc: "",
    country_ids: null,
    // status: "draft",
  });

  const [countryOptions, setCountryOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryPage, setCountryPage] = useState(1);
  const [countryTotalPages, setCountryTotalPages] = useState(1);
  const [countryLoading, setCountryLoading] = useState(false);

  const hasFetched = useRef(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Generic input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log("Input Change:", name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ For country dropdown
  const handleCountryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      country_ids: selectedOption,
    }));
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.desc.trim()) newErrors.desc = "Description is required";
    if (!formData.country_ids) newErrors.country = "Country is required";
    return newErrors;
  };

  // ✅ Fetch countries
  const fetchCountry = async (pageNumber = 1, query = "") => {
    if (pageNumber > countryTotalPages) return; // no more pages
    try {
      setCountryLoading(true);
      const limit = 10; // set per-page limit
      const response = await getCountries(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: c.name }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCountryOptions(options);
      } else {
        // append for scroll
        setCountryOptions((prev) => [...prev, ...options]);
      }

      setCountryPage(pagination?.page || pageNumber);
      setCountryTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  // ✅ Fetch FAQ in edit mode
  useEffect(() => {
    const fetchFaq = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await getFaqById(id);
        if (res.data?.success) {
          const faq = res.data.data;

          // ✅ Convert stored country_ids to selected options (after fetching countries)
          const selectedCountries =
            (faq.country_ids || [])
              .map((id) => {
                const country = countryOptions.find((c) => c.value === id);
                return country
                  ? country
                  : { value: id, label: `Country ${id}` }; // fallback label
              })
              .filter(Boolean) || [];

          setFormData({
            title: faq.title || "",
            desc: faq.desc || "",
            country_ids: selectedCountries,
            status: faq.status || true,
          });
        } else {
          Swal.fire(
            "Error",
            res.data?.message || "Failed to fetch FAQ",
            "error"
          );
        }
      } catch (err) {
        console.error("Error fetching FAQ:", err);
        Swal.fire("Error", "Something went wrong fetching FAQ", "error");
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCountry(); // ✅ preload countries first
      fetchFaq();
    }
  }, [id]);

  // ✅ Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        desc: formData.desc,
        country_ids: formData.country_ids?.map((c) => c.value) || [],
        // status: formData.status,
      };

      const response = id
        ? await updateFaq(id, payload)
        : await createFaq(payload);

      if (response.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response.data?.message ||
            `FAQ ${id ? "updated" : "added"} successfully!`,
        });
        navigate("/faq", {
          state: {
            page: location.state?.page || 1,
            searchTerm: location.state?.searchTerm || "",
          },
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.message || "Failed to save FAQ",
        });
      }
    } catch (err) {
      console.error("Error submitting FAQ:", err);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit FAQ" : "Add FAQ"}`}
        motherMenu="FAQ"
        activeMenuLink={`${id ? `edit-faq/${id}` : "add-faq"}`}
        motherMenuLink={"faq"}
      />

      <div className="row">
        <div className="col-xl-12">
          <div className="card h-auto">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div
                  onClick={() => navigate("/faq", {
                    state: {
                      page: location.state?.page || 1,
                      searchTerm: location.state?.searchTerm || "",
                    },
                  })}
                  className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                >
                  <i className="fas fa-chevron-left"></i>
                  <div>FAQ List</div>
                </div>

                {/* Title */}
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    name="title"
                    className={`form-control ${errors.title ? "is-invalid" : ""
                      }`}
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter FAQ title"
                  />
                  {errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>

                {/* Description */}
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="desc"
                    className={`form-control ${errors.desc ? "is-invalid" : ""
                      }`}
                    value={formData.desc}
                    onChange={handleChange}
                    placeholder="Enter FAQ description"
                    rows={4}
                  ></textarea>
                  {errors.desc && (
                    <div className="invalid-feedback">{errors.desc}</div>
                  )}
                </div>

                {/* Country */}
                <div className="mb-3">
                  <label className="form-label">Country</label>
                  <Select
                    isMulti
                    value={formData.country_ids}
                    onChange={handleCountryChange}
                    options={countryOptions}
                    placeholder={
                      countryLoading ? "Loading countries..." : "Select country"
                    }
                    isLoading={countryLoading}
                    menuPlacement="auto"
                    menuPosition="fixed"
                    onMenuScrollToBottom={() => fetchCountry(countryPage + 1)}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable={false}
                    isSearchable={false}
                    components={multiSelectComponents}
                  />
                  {errors.country && (
                    <div className="text-danger mt-1">{errors.country}</div>
                  )}
                </div>

                {/* Status */}
                {/* <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div> */}

                {/* Buttons */}
                <hr style={{ margin: "0px" }} className="mx-2" />
                <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      navigate("/faq", {
                        state: {
                          page: location.state?.page || 1,
                          searchTerm: location.state?.searchTerm || "",
                        },
                      })
                    }}
                  >
                    Back
                  </Button>
                  <Button type="submit" className="btn btn-primary btn-sm">
                    {id ? "Update" : "Save"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddFaq;
